/**
 * Axios-based API client with interceptors (mirrors apps/admin-ui/src/lib/api.ts).
 * - Request: attaches Bearer token from localStorage
 * - Response: handles 401 (logout + login redirect) for **application** calls only
 */

import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { getToken, clearAuth, markSessionExpired } from './auth';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach Bearer token ────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ── Response interceptor: handle 401 ────────────────────────────────────────

/**
 * The auth endpoints own their own 401 handling: a failed `POST /auth/refresh`
 * already clears the session and flags the expiry notice (`lib/auth.ts`), and a
 * 401 from `POST /auth/login` is just wrong credentials — it must never wipe a
 * session or hijack the location while the login page is up. Everything else
 * falling through here is a hard logout.
 */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  const path = url.replace(/^https?:\/\/[^/]+/, '').split('?')[0];
  return /^\/auth\/(login|refresh|logout)$/.test(path);
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ message?: string; statusCode?: number }>) => {
    const url = error.config?.url;
    if (error.response?.status === 401 && !isAuthEndpoint(url)) {
      clearAuth();
      markSessionExpired();
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search + window.location.hash,
        );
        window.location.href = `/login?returnTo=${returnTo}`;
      }
    }
    return Promise.reject(error);
  },
);
